/**
 * ThemeToggle — sun/moon button toggling dark ↔ light by adding/removing the
 * `.dark` class on <html> and persisting to localStorage. A generic UI
 * primitive (lives alongside Button/Card/Switch), not marketing chrome.
 * Styled with Tailwind utilities mapped to design tokens — no bespoke CSS.
 */
import { useCallback, useEffect, useState } from 'react';
import { Sun, Moon } from 'lucide-react';
import { cn } from '../utils';

type Theme = 'dark' | 'light';

function getStoredTheme(): Theme {
  try {
    const stored = localStorage.getItem('inferrex-theme');
    if (stored === 'light' || stored === 'dark') return stored;
  } catch {
    /* SSR / private browsing */
  }
  return 'dark';
}

function applyTheme(theme: Theme): void {
  const root = document.documentElement;
  root.classList.toggle('dark', theme === 'dark');
  try {
    localStorage.setItem('inferrex-theme', theme);
  } catch {
    /* SSR / private browsing */
  }
}

export function useTheme() {
  const [theme, setThemeState] = useState<Theme>(getStoredTheme);

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  const toggle = useCallback(() => {
    setThemeState((prev) => (prev === 'dark' ? 'light' : 'dark'));
  }, []);

  return { theme, toggle } as const;
}

export interface ThemeToggleProps {
  readonly theme: Theme;
  readonly onToggle: () => void;
  readonly className?: string;
}

export function ThemeToggle({ theme, onToggle, className }: ThemeToggleProps) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
      className={cn(
        'inline-flex size-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        className,
      )}
    >
      {theme === 'dark' ? <Sun className="size-4" /> : <Moon className="size-4" />}
    </button>
  );
}
